import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, Activity, BarChart3, Home, Building2, Trees, KeyRound, TrendingUp, DollarSign,
  ShieldCheck, MapPinned, Award, Lock, ArrowRight, Sparkles,
} from "lucide-react";
import { homesFeaturedCities } from "@/lib/api";
import { PROPERTY_TYPES } from "@/components/homes/homesUtil";

const IMG_HOUSE = "https://images.unsplash.com/photo-1777106322601-578dc9213ace?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const IMG_COMMERCIAL = "https://images.unsplash.com/photo-1621831337128-35676ca30868?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const IMG_LAND = "https://images.unsplash.com/photo-1758612113449-a7c2f814b2e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const PRICE_RANGES = [
  { label: "Any", min: "", max: "" },
  { label: "Under $300K", min: "", max: "300000" },
  { label: "$300K – $600K", min: "300000", max: "600000" },
  { label: "$600K – $1M", min: "600000", max: "1000000" },
  { label: "$1M+", min: "1000000", max: "" },
];

export default function HomesLanding() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [ptype, setPtype] = useState("");
  const [txn, setTxn] = useState("for_sale");
  const [price, setPrice] = useState(0);
  const [cities, setCities] = useState([]);

  useEffect(() => { homesFeaturedCities(8).then((d) => setCities(d.cities)).catch(() => {}); }, []);

  const submit = (e) => {
    e?.preventDefault();
    const pr = PRICE_RANGES[price];
    const p = new URLSearchParams({ status: txn });
    if (q.trim()) p.set("q", q.trim());
    if (ptype) p.set("property_type", ptype);
    if (pr.min) p.set("min_price", pr.min);
    if (pr.max) p.set("max_price", pr.max);
    nav(`/homes/search?${p.toString()}`);
  };

  return (
    <div data-testid="homes-landing">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white pt-12">
        <div className="mx-auto grid max-w-[1400px] items-center gap-10 px-4 pb-4 lg:grid-cols-2">
          {/* Left */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#EEF2FF] px-3.5 py-1.5 text-sm font-semibold text-[#2563EB]" data-testid="homes-hero-pill">
              <Activity className="h-4 w-4" /> Your Pulse <span className="text-slate-500">on Real Estate</span>
            </span>
            <h1 className="mt-6 text-5xl font-black leading-[1.02] tracking-tight text-[#0F172A] md:text-7xl">
              Real Estate<br />
              <span className="text-[#2563EB]">Insights.</span><br />
              <span className="text-[#2563EB]">Opportunities.</span><br />
              Results.
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-slate-600">
              HomesPulse.pro delivers real-time data, community insights and powerful tools to help you buy, sell, rent or invest with confidence across the USA.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => nav("/homes/search?status=for_sale")} className="rounded-xl bg-[#2563EB] px-7 py-3.5 font-bold text-white shadow-lg shadow-[#2563EB]/20 transition-colors hover:bg-[#1D4ED8]" data-testid="homes-explore-btn">
                Explore Properties
              </button>
              <Link to="/homes/agents" className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-6 py-3.5 font-bold text-[#0F172A] transition-colors hover:bg-slate-50" data-testid="homes-insights-btn">
                <BarChart3 className="h-5 w-5 text-[#2563EB]" /> Find an Agent
              </Link>
            </div>
            <Link to="/homes/advisor" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#2563EB] hover:underline" data-testid="homes-advisor-cta">
              <Sparkles className="h-4 w-4" /> Not sure where? Ask "Where should I live?"
            </Link>
          </motion.div>

          {/* Right — 3-image composition */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="relative hidden lg:block">
            <div className="absolute right-0 top-0 h-24 w-24 opacity-40" style={{ backgroundImage: "radial-gradient(#2563EB 1.4px, transparent 1.4px)", backgroundSize: "12px 12px" }} />
            <div className="relative grid grid-cols-2 gap-4">
              <div className="mt-10">
                <ImgCard src={IMG_HOUSE} icon={Home} title="Homes" sub="Find your perfect home" h="h-56" />
              </div>
              <div>
                <ImgCard src={IMG_COMMERCIAL} icon={Building2} title="Commercial" sub="Grow your business" h="h-72" />
              </div>
              <div className="col-span-2">
                <ImgCard src={IMG_LAND} icon={Trees} title="Land" sub="Build your future" h="h-44" wide />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search bar */}
        <div className="mx-auto max-w-[1400px] px-4 pb-6">
          <form onSubmit={submit} className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-xl md:flex-row md:items-center" data-testid="homes-search-bar">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by City, State, ZIP, or Address"
                className="w-full rounded-xl bg-transparent py-3 pl-12 pr-3 text-[#0F172A] outline-none" data-testid="homes-hero-search" />
            </div>
            <Sel label="Property Type" value={ptype} onChange={setPtype} options={PROPERTY_TYPES.map((t) => [t.v, t.label])} testid="homes-hero-ptype" />
            <Sel label="Transaction" value={txn} onChange={setTxn} options={[["for_sale", "Buy"], ["for_rent", "Rent"]]} testid="homes-hero-txn" />
            <Sel label="Price Range" value={String(price)} onChange={(v) => setPrice(Number(v))} options={PRICE_RANGES.map((r, i) => [String(i), r.label])} testid="homes-hero-price" />
            <button type="submit" className="rounded-xl bg-[#2563EB] px-8 py-3 font-bold text-white transition-colors hover:bg-[#1D4ED8]" data-testid="homes-hero-search-btn">Search</button>
          </form>
        </div>

        {/* Category strip */}
        <div className="mx-auto max-w-[1400px] px-4 pb-10">
          <div className="grid grid-cols-2 gap-x-6 gap-y-5 border-t border-slate-100 pt-6 sm:grid-cols-3 lg:grid-cols-6" data-testid="homes-categories">
            <Cat icon={Home} title="Residential" sub="Houses, Condos, Townhomes" to="/homes/search?property_type=single_family" />
            <Cat icon={Building2} title="Commercial" sub="Office, Retail, Industrial" to="/homes/search?property_type=multifamily" />
            <Cat icon={Trees} title="Land" sub="Residential, Agricultural" to="/homes/search?property_type=land" />
            <Cat icon={KeyRound} title="Rentals" sub="Apartments, Homes" to="/homes/search?status=for_rent" />
            <Cat icon={TrendingUp} title="Market Insights" sub="Trends, Reports, Analytics" to="/homes" />
            <Cat icon={DollarSign} title="Invest" sub="Find opportunities & ROI" to="/homes/search?status=for_sale" />
          </div>
        </div>

        {/* Trust bar */}
        <div className="bg-[#EEF2FF]">
          <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-6 px-4 py-6 lg:grid-cols-4" data-testid="homes-trust">
            <Trust icon={ShieldCheck} title="Trusted Data" sub="Accurate. Reliable. Up-to-date." />
            <Trust icon={MapPinned} title="USA Coverage" sub="Nationwide data and local insights." />
            <Trust icon={Award} title="Expert Insights" sub="Make smarter real estate decisions." />
            <Trust icon={Lock} title="Secure & Private" sub="Your data is safe with us." />
          </div>
        </div>
      </section>

      {/* Featured cities */}
      <section className="mx-auto max-w-[1400px] px-4 py-16">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 className="text-3xl font-black text-[#0F172A]">Explore homes by city</h2>
            <p className="mt-1 text-slate-500">Every city page blends listings with real community intelligence.</p>
          </div>
          <Link to="/explore/states" className="hidden items-center gap-1.5 text-sm font-semibold text-[#2563EB] hover:underline sm:flex">All places <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="homes-featured-cities">
          {cities.map((c) => (
            <Link key={c.geo_id} to={`/homes/place/${c.geo_id}`} className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-[#0F172A]" data-testid={`homes-city-${c.geo_id}`}>
              <div className="h-40 w-full overflow-hidden">
                {c.image_url ? (
                  <img src={c.image_url} alt={c.name} loading="lazy" className="h-full w-full object-cover opacity-75 transition-transform duration-500 group-hover:scale-110" />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-[#2563EB] to-[#0F172A]" />
                )}
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <div className="text-lg font-black text-white">{c.name}</div>
                <div className="text-xs text-white/70">{c.state_name} · {c.listings} listings</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function ImgCard({ src, icon: Icon, title, sub, h, wide }) {
  return (
    <div className={`relative ${h} w-full overflow-hidden rounded-2xl shadow-lg`}>
      <img src={src} alt={title} className="h-full w-full object-cover" />
      <div className={`absolute ${wide ? "left-3 top-3" : "left-3 top-3"} flex items-center gap-2 rounded-xl bg-white/95 px-3 py-2 shadow-md backdrop-blur`}>
        <Icon className="h-5 w-5 text-[#2563EB]" />
        <span><span className="block text-sm font-bold leading-tight text-[#0F172A]">{title}</span><span className="text-[11px] text-slate-500">{sub}</span></span>
      </div>
    </div>
  );
}

function Sel({ label, value, onChange, options, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 px-3 py-2 md:border-0">
      <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent text-sm font-semibold text-[#0F172A] outline-none" data-testid={testid}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function Cat({ icon: Icon, title, sub, to }) {
  return (
    <Link to={to} className="group flex items-start gap-3" data-testid={`homes-cat-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#EEF2FF] transition-colors group-hover:bg-[#2563EB]">
        <Icon className="h-5 w-5 text-[#2563EB] transition-colors group-hover:text-white" />
      </span>
      <span><span className="block font-bold text-[#0F172A]">{title}</span><span className="text-xs text-slate-500">{sub}</span></span>
    </Link>
  );
}

function Trust({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-6 w-6 shrink-0 text-[#2563EB]" />
      <span><span className="block font-bold text-[#0F172A]">{title}</span><span className="text-xs text-slate-500">{sub}</span></span>
    </div>
  );
}
